// Finance › QuickBooks (Stephanie 2026-10-13: "In invoicing is where we
// usually export them to QuickBooks" … "Bills as well"; v2 = plan T8, reworked
// after the ux review of 2026-10-21).
//
// Two tabs:
//   • Export — invoices, bills, payments, credits, card refunds or Stripe
//     payouts. Ready rows are ticked; a row that didn't go through last time is
//     unticked (tick it to try again); a held row can't be ticked and says
//     why. The server does every sum (the browser never adds money up) and
//     refuses anything not ready to post. A send asks once before it goes.
//   • Needs attention — everything that needs a person, what to do first, and
//     Retry / Check again / I've done this (qbo/SyncIssuesPanel.tsx).
//
// SENDING IS SWITCHED OFF while commonsku still posts to the books — sending
// from here too would record the same money twice. The status endpoint says so
// (exportAllowed / exportLockedReason) and the Send button stays disabled with
// that reason, on both tabs, until Jake switches it on. The server refuses
// regardless. The same reason disables Retry for the real books.
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Lock, Send, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { cn } from "@/lib/utils";
import { SyncIssuesPanel, syncIssuesQueryKey } from "@/pages/qbo/SyncIssuesPanel";
import {
  BACK_LINK,
  docLabel,
  ENTITIES,
  entityFromParam,
  errText,
  formatDate,
  formatDateTime,
  NOUN,
  TEST_COMPANY_ONLY,
  type Entity,
  type ExportCounts,
  type ExportResponse,
  type HistoryRow,
  type QboStatus,
  type Queue,
  type QueueRow,
  type RowResult,
  type SyncIssueList,
} from "@/pages/qbo/types";

type Tab = "export" | "issues";

/** Table header: 14px at the smallest (ux review #25). */
const HEAD = "border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[14px] font-medium text-muted";

/** The pills in table cells — words first, colour backs them up. */
const PILL = "inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-[14px] font-medium";
const PILL_TONE = {
  ready: "bg-[#f1f1ee] text-ink",
  ok: "bg-ok-bg text-ok",
  warn: "bg-warn-bg text-warn",
  risk: "bg-risk-bg text-risk",
} as const;

function issueHref(id: string, entity: Entity): string {
  return `/finance/quickbooks?tab=issues&entity=${entity}#issue-${id}`;
}

export function QuickBooksExportPage() {
  const { user } = useAuth();
  const canManage = user?.roles.some((r) => r === "admin" || r === "finance") ?? false;
  const isAdmin = user?.roles.includes("admin") ?? false;
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "issues" ? "issues" : "export";
  const entity: Entity = entityFromParam(params.get("entity"));
  const noun = NOUN[entity];
  const queryClient = useQueryClient();

  const statusQ = useQuery({
    queryKey: ["qbo", "status"],
    queryFn: () => api<QboStatus>("/api/finance/qbo/status"),
    enabled: canManage,
  });
  const queueQ = useQuery({
    queryKey: ["qbo", "queue", entity],
    queryFn: () => api<Queue>(`/api/finance/qbo/export/queue?entity=${entity}`),
    enabled: canManage && tab === "export",
  });
  const historyQ = useQuery({
    queryKey: ["qbo", "history", entity],
    queryFn: () => api<{ rows: HistoryRow[] }>(`/api/finance/qbo/export/history?entity=${entity}`),
    enabled: canManage && tab === "export",
  });
  const countsQ = useQuery({
    queryKey: ["qbo", "counts"],
    queryFn: () => api<ExportCounts>("/api/finance/qbo/export/counts"),
    enabled: canManage,
    // Six queue loads on the server — don't refetch on every tab focus.
    staleTime: 60_000,
  });
  // The tab badge: how many things need a person in the real books.
  const issuesQ = useQuery({
    queryKey: syncIssuesQueryKey("production"),
    queryFn: () => api<SyncIssueList>("/api/finance/qbo/issues"),
    enabled: canManage,
  });

  const rows = queueQ.data?.rows ?? [];
  const readyIds = useMemo(() => rows.filter((r) => (r.state ?? "not_sent") === "not_sent").map((r) => r.id), [rows]);
  // null = the default selection: every READY row (a row that didn't go last
  // time is unticked; a held row can't be ticked).
  const [picked, setPicked] = useState<Set<string> | null>(null);
  useEffect(() => setPicked(null), [entity]);
  const selected = picked ?? new Set(readyIds);
  const selectedIds = useMemo(
    () => rows.filter((r) => r.sendable !== false && selected.has(r.id)).map((r) => r.id),
    [rows, selected],
  );
  const sendableCount = rows.filter((r) => r.sendable !== false).length;
  const isDefaultSelection =
    selectedIds.length === readyIds.length && readyIds.every((id) => selected.has(id));

  // The selection's total comes from the server (the browser never adds money).
  const selTotalQ = useQuery({
    queryKey: ["qbo", "selection-total", entity, selectedIds],
    queryFn: () =>
      api<{ count: number; total: string }>("/api/finance/qbo/export/selection-total", {
        method: "POST",
        body: JSON.stringify({ entity, ids: selectedIds }),
      }),
    enabled: canManage && !isDefaultSelection && selectedIds.length > 0,
  });
  const selectionTotal = selectedIds.length === 0
    ? null
    : isDefaultSelection
      ? (queueQ.data?.ready?.total ?? queueQ.data?.total)
      : selTotalQ.data?.total;

  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<ExportResponse | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () =>
      api<ExportResponse>("/api/finance/qbo/export", {
        method: "POST",
        body: JSON.stringify({ entity, ids: selectedIds }),
      }),
    onSuccess: (res) => {
      setResult(res);
      setSendError(null);
      setConfirming(false);
      setPicked(null);
      void queryClient.invalidateQueries({ queryKey: ["qbo"] });
    },
    onError: (e) => {
      setConfirming(false);
      setSendError(errText(e, "Nothing was sent to QuickBooks. Try again in a minute."));
    },
  });

  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedIds.length > 0 && selectedIds.length < sendableCount;
    }
  }, [selectedIds.length, sendableCount]);

  if (!canManage) {
    return (
      <div className="mx-auto w-full">
        <PageHeader eyebrow="Finance / QuickBooks" title="QuickBooks" />
        <p className="rounded-md border border-rule bg-white p-5 text-[15px] text-muted">
          QuickBooks is available to finance and admin users.
        </p>
      </div>
    );
  }

  const st = statusQ.data;
  const locked = st ? st.exportAllowed !== true : true;
  const notConnected = st ? !st.configured || !st.connected : false;
  const blockedReason = !st
    ? null
    : !st.configured || !st.connected
      ? "QuickBooks isn't connected, so nothing can be sent. Ask Jake to connect it."
      : st.exportAllowed !== true
        ? (st.exportLockedReason ?? "Sending to QuickBooks is off for now.")
        : null;
  const canSend = !!st && !locked && !notConnected && selectedIds.length > 0 && !send.isPending;
  const resultById = new Map((result?.results ?? []).map((r) => [r.id, r]));
  const issues = issuesQ.data;
  const issueCount = issues?.count ?? 0;
  const urgentCount = issues ? issues.issues.filter((i) => issues.urgent.includes(i.category)).length : 0;

  const go = (next: { tab?: Tab; entity?: Entity }) => {
    setResult(null);
    setSendError(null);
    setConfirming(false);
    const t = next.tab ?? tab;
    const e = next.entity ?? entity;
    setParams(t === "issues" ? { tab: "issues", entity: e } : { entity: e });
  };
  const toggle = (id: string) =>
    setPicked(() => {
      const n = new Set(selected);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const isPayouts = entity === "payouts";

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Finance / QuickBooks"
        title="QuickBooks"
        intro="Send invoices, bills, payments, credits, card refunds and Stripe payouts to QuickBooks — each goes once, and only when it's ready. If something doesn't go through, nothing is sent and nothing is duplicated; Needs attention says what to do."
        action={
          <Link
            to={BACK_LINK[entity].to}
            className="inline-flex h-10 items-center rounded-md border border-rule bg-white px-[14px] text-[15px] font-medium text-ink hover:bg-canvas"
          >
            Back to {BACK_LINK[entity].label}
          </Link>
        }
      />

      {confirmation && (
        <div role="status" className="mb-5 flex items-start justify-between gap-3 rounded-md border border-ok/30 bg-ok-bg px-4 py-3 text-[15px] text-ok">
          <p className="inline-flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {confirmation}
          </p>
          <button type="button" aria-label="Dismiss" onClick={() => setConfirmation(null)} className="text-ok hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {statusQ.isError && (
        <div role="alert" className="mb-5 rounded-md border border-risk/30 bg-risk-bg px-4 py-3 text-[15px] text-risk">
          Flow couldn't check the QuickBooks connection just now, so sending is paused. Refresh the page in a moment.
        </div>
      )}

      {blockedReason && (
        <div role="status" className="mb-5 flex items-start gap-3 rounded-md border border-warn/30 bg-warn-bg px-4 py-3 text-[15px] text-ink">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
          <p>
            {blockedReason}
            {tab === "issues" && " Retry is switched off until then."}
            {notConnected && (
              <>
                {" "}
                <Link to="/finance/dashboard" className="font-medium underline underline-offset-2">
                  Go to the Finance page
                </Link>
              </>
            )}
          </p>
        </div>
      )}

      <div role="tablist" aria-label="QuickBooks" className="mb-6 flex gap-2 border-b border-rule">
        {([
          ["export", "Export"],
          ["issues", "Needs attention"],
        ] as Array<[Tab, string]>).map(([t, label]) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => go({ tab: t })}
            className={cn(
              "-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-[16px] font-semibold",
              tab === t ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink",
            )}
          >
            {label}
            {t === "issues" && issueCount > 0 && (
              <span
                aria-label={`${issueCount} ${issueCount === 1 ? "thing needs" : "things need"} attention${urgentCount ? `, ${urgentCount} urgent` : ""}`}
                className={cn(
                  "rounded-full px-2 py-0.5 text-[14px] font-semibold",
                  urgentCount > 0 ? "bg-risk-bg text-risk" : "bg-warn-bg text-warn",
                )}
              >
                {issueCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "issues" ? (
        <SyncIssuesPanel isAdmin={isAdmin} retryBlockedReason={blockedReason} onConfirm={setConfirmation} />
      ) : (
        <>
          <div role="tablist" aria-label="What to export" className="mb-5 flex flex-wrap gap-2">
            {ENTITIES.map((e) => {
              const c = countsQ.data?.[e];
              return (
                <button
                  key={e}
                  type="button"
                  role="tab"
                  aria-selected={entity === e}
                  aria-label={`${NOUN[e].Title}${c ? `: ${c.ready} ready${c.failed ? `, ${c.failed} didn't go through` : ""}` : ""}`}
                  onClick={() => go({ tab: "export", entity: e })}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[15px] font-medium",
                    entity === e ? "border-ink bg-ink text-white" : "border-rule bg-white text-ink hover:bg-canvas",
                  )}
                >
                  {NOUN[e].Title}
                  {c && c.ready > 0 && (
                    <span className={cn("tabular-nums", entity === e ? "text-white/80" : "text-muted")}>{c.ready}</span>
                  )}
                  {c && c.failed > 0 && <span className="h-2 w-2 rounded-full bg-risk" aria-hidden />}
                </button>
              );
            })}
          </div>

          {sendError && (
            <div role="alert" className="mb-5 rounded-md border border-risk/30 bg-risk-bg px-4 py-3 text-[15px] text-risk">
              {sendError}
            </div>
          )}
          {result && <ResultBanner result={result} noun={noun} onIssues={() => go({ tab: "issues" })} />}

          <section className="mb-10">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold tracking-[-0.4px] text-ink">Ready to send</h2>
                {queueQ.data && (
                  <>
                    <p className="text-[15px] text-ink">
                      {(queueQ.data.ready?.count ?? queueQ.data.count) === 0
                        ? `No ${noun.many} ready to send`
                        : `${queueQ.data.ready?.count ?? queueQ.data.count} ${(queueQ.data.ready?.count ?? queueQ.data.count) === 1 ? noun.one : noun.many} · ${queueQ.data.ready?.total ?? queueQ.data.total}`}
                    </p>
                    {((queueQ.data.failedCount ?? 0) > 0 || (queueQ.data.heldCount ?? 0) > 0) && (
                      <p className="text-[14px] text-muted">
                        {[
                          queueQ.data.failedCount ? `${queueQ.data.failedCount} didn't go through last time` : null,
                          queueQ.data.heldCount ? `${queueQ.data.heldCount} on hold` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-3">
                  {selectionTotal != null && (
                    <p className="text-[15px] text-ink">
                      {selectedIds.length} selected · <span className="font-semibold tabular-nums">{selectionTotal}</span>
                    </p>
                  )}
                  <Button
                    type="button"
                    disabled={!canSend}
                    onClick={() => setConfirming(true)}
                    title={blockedReason ?? undefined}
                  >
                    <Send className="h-4 w-4" />
                    {send.isPending
                      ? "Sending…"
                      : selectedIds.length === 0
                        ? "Select something to send"
                        : `Send ${selectedIds.length} to QuickBooks`}
                  </Button>
                </div>
                {blockedReason && <p className="text-[14px] text-muted">Sending is off for now — see the note above.</p>}
              </div>
            </div>

            {confirming && canSend && (
              <div role="dialog" aria-label="Confirm send" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-ink/20 bg-white px-4 py-3">
                <p className="text-[15px] text-ink">
                  Send {selectedIds.length} {selectedIds.length === 1 ? noun.one : noun.many}
                  {selectionTotal ? ` (${selectionTotal})` : " (working out the total…)"} to QuickBooks? Each goes once — anything already there is matched, not duplicated.
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => send.mutate()}
                    disabled={send.isPending || selectionTotal == null}
                    title={selectionTotal == null ? "Working out the total…" : undefined}
                  >
                    <Send className="h-4 w-4" /> Send now
                  </Button>
                </div>
              </div>
            )}

            {queueQ.isLoading ? (
              <div className={`${TABLE_WRAP} h-24 animate-pulse`} aria-label="Loading" />
            ) : queueQ.isError ? (
              <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-4 text-[15px] text-risk">
                {errText(queueQ.error, "Couldn't load the export list — try again in a moment.")}
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-md border border-rule bg-white px-5 py-10 text-center">
                <p className="text-lg font-medium text-ink">Nothing waiting to go to QuickBooks.</p>
                <p className="mt-1 text-[15px] text-muted">{noun.empty}</p>
              </div>
            ) : (
              <div className={TABLE_WRAP} role="region" aria-label={`${noun.Title} ready to send`} tabIndex={0}>
                <table className="w-full min-w-[760px] text-left">
                  <thead className={HEAD}>
                    <tr>
                      <th scope="col" className={cn(TH, "w-10")}>
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          aria-label="Select everything that can be sent"
                          className="h-4 w-4 accent-ink"
                          checked={sendableCount > 0 && selectedIds.length === sendableCount}
                          onChange={(e) =>
                            setPicked(
                              e.target.checked
                                ? new Set(rows.filter((r) => r.sendable !== false).map((r) => r.id))
                                : new Set(),
                            )
                          }
                        />
                      </th>
                      <th scope="col" className={TH}>{noun.doc}</th>
                      {!isPayouts && <th scope="col" className={TH}>{noun.who}</th>}
                      {!isPayouts && <th scope="col" className={TH}>Date</th>}
                      {isPayouts && <th scope="col" className={TH}>{noun.who}</th>}
                      <th scope="col" className={TH}>Where it stands</th>
                      <th scope="col" className={cn(TH, "text-right")}>Amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-[15px]">
                    {rows.map((r) => {
                      const sendable = r.sendable !== false;
                      return (
                        <tr
                          key={r.id}
                          className={cn(TROW, sendable ? "cursor-pointer" : "opacity-80")}
                          onClick={(e) => {
                            if (!sendable || (e.target as HTMLElement).closest("a,input,button")) return;
                            toggle(r.id);
                          }}
                        >
                          <td className={TD}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${docLabel(r.docNumber)}`}
                              className="h-4 w-4 accent-ink disabled:cursor-not-allowed"
                              disabled={!sendable}
                              title={sendable ? undefined : `Can't be sent yet: ${r.stateLabel ?? "on hold"}`}
                              checked={sendable && selected.has(r.id)}
                              onChange={() => toggle(r.id)}
                            />
                          </td>
                          <td className={cn(TD, "font-medium text-ink")}>
                            {isPayouts ? formatDate(r.docNumber) : docLabel(r.docNumber)}
                            {!isPayouts && r.detail && entity !== "payments" && (
                              <span className="mt-0.5 block text-[14px] font-normal text-muted">{r.detail}</span>
                            )}
                            {entity === "payments" && r.detail?.startsWith("Upfront") && (
                              <span className="mt-0.5 block text-[14px] font-normal text-muted">{r.detail}</span>
                            )}
                          </td>
                          {!isPayouts && <td className={cn(TD, "text-ink")}>{r.name}</td>}
                          {!isPayouts && <td className={cn(TD, "whitespace-nowrap text-muted")}>{formatDate(r.date)}</td>}
                          {isPayouts && <td className={cn(TD, "text-ink")}>{r.detail ?? "Stripe payout"}</td>}
                          <td className={cn(TD, "max-w-[560px]")}>
                            <StandingCell row={r} res={resultById.get(r.id)} entity={entity} ticked={selected.has(r.id)} />
                          </td>
                          <td className={cn(TD, "text-right font-medium tabular-nums text-ink")}>{r.amount}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <RecentActivity rows={historyQ.data?.rows ?? []} loading={historyQ.isLoading} entity={entity} />
        </>
      )}
    </div>
  );
}

/** What happened to the last send — one clause per outcome, never "Sent 0"
 *  (ux review #9). */
function ResultBanner({ result, noun, onIssues }: { result: ExportResponse; noun: (typeof NOUN)[Entity]; onIssues: () => void }) {
  const n = (k: number) => `${k} ${k === 1 ? noun.one : noun.many}`;
  const clauses: Array<{ text: string; tone: "ok" | "warn" | "risk"; issues?: boolean }> = [];
  if (result.sent > 0) clauses.push({ text: `${n(result.sent)} sent — in your books now.`, tone: "ok" });
  if (result.sentToSandbox) clauses.push({ text: `${n(result.sentToSandbox)} sent to the test company only — still waiting for your books.`, tone: "warn" });
  if (result.needsTaxCheck) clauses.push({ text: `${n(result.needsTaxCheck)} reached QuickBooks, but the tax there differs.`, tone: "warn", issues: true });
  if (result.held) clauses.push({ text: `${n(result.held)} on hold — each row says what it's waiting for.`, tone: "warn" });
  if (result.failed) clauses.push({ text: `${n(result.failed)} didn't go through — each row says why.`, tone: "risk", issues: true });
  if (clauses.length === 0) clauses.push({ text: "Nothing was sent.", tone: "warn" });
  const allOk = clauses.every((c) => c.tone === "ok");
  return (
    <div
      role="status"
      className={cn(
        "mb-5 rounded-md border px-4 py-3 text-[15px]",
        allOk ? "border-ok/30 bg-ok-bg text-ok" : "border-warn/30 bg-warn-bg text-ink",
      )}
    >
      <ul className="space-y-1">
        {clauses.map((c) => (
          <li key={c.text} className={cn(c.tone === "risk" && "text-risk", c.tone === "ok" && !allOk && "text-ok")}>
            {c.text}
            {c.issues && (
              <>
                {" "}
                <button type="button" onClick={onIssues} className="font-medium underline underline-offset-2">
                  See what to do in Needs attention
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhyLink({ id, entity }: { id: string; entity: Entity }) {
  return (
    <Link to={issueHref(id, entity)} className="whitespace-nowrap text-[14px] font-medium text-ink underline underline-offset-2">
      Why, and what to do →
    </Link>
  );
}

/** Where a row stands: the result of this send if there is one, otherwise the
 *  server's own plain label. */
function StandingCell({ row, res, entity, ticked }: { row: QueueRow; res: RowResult | undefined; entity: Entity; ticked: boolean }) {
  if (res) {
    if (res.ok && res.taxMismatch) {
      return (
        <span className="flex flex-col items-start gap-1">
          <span className={cn(PILL, PILL_TONE.warn)}>Sent — tax differs</span>
          <WhyLink id={row.id} entity={entity} />
        </span>
      );
    }
    if (res.ok && res.stamped === false) {
      return <span className="text-warn">{TEST_COMPANY_ONLY}</span>;
    }
    if (res.ok) {
      return (
        <span className={cn(PILL, PILL_TONE.ok, "gap-1")}>
          <CheckCircle2 className="h-4 w-4" /> {res.deduped ? "Already in QuickBooks" : "Sent"}
        </span>
      );
    }
    if (res.held) {
      return (
        <span className="flex flex-col items-start gap-1">
          <span className={cn(PILL, PILL_TONE.warn)}>On hold</span>
          <span className="text-ink">{res.error ?? "Waiting"}</span>
        </span>
      );
    }
    return (
      <span className="flex flex-col items-start gap-1">
        <span className={cn(PILL, PILL_TONE.risk)}>Didn't go through</span>
        <WhyLink id={row.id} entity={entity} />
      </span>
    );
  }
  if (row.state === "held") {
    return (
      <span className="flex flex-col items-start gap-1">
        <span className={cn(PILL, PILL_TONE.warn)}>{row.stateLabel ?? "On hold"}</span>
        <span className="text-[14px] text-muted">Can't be sent until then.</span>
      </span>
    );
  }
  if (row.state === "failed" || !!row.lastError) {
    return (
      <span className="flex flex-col items-start gap-1">
        <span className={cn(PILL, PILL_TONE.risk)}>Didn't go through</span>
        {row.lastErrorHeadline && <span className="text-ink">{row.lastErrorHeadline}</span>}
        <WhyLink id={row.id} entity={entity} />
        {!ticked && <span className="text-[14px] text-muted">Last try didn't go through. Tick to try again.</span>}
      </span>
    );
  }
  return <span className={cn(PILL, PILL_TONE.ready)}>Ready to send</span>;
}

/** Recent activity, grouped by send (events within a couple of minutes of
 *  each other), newest first (ux review #19). */
function RecentActivity({ rows, loading, entity }: { rows: HistoryRow[]; loading: boolean; entity: Entity }) {
  const noun = NOUN[entity];
  const groups: HistoryRow[][] = [];
  for (const h of rows.slice(0, 30)) {
    const last = groups[groups.length - 1];
    const prev = last?.[last.length - 1];
    if (prev && Math.abs(new Date(prev.occurredAt).getTime() - new Date(h.occurredAt).getTime()) <= 2 * 60 * 1000) last.push(h);
    else groups.push([h]);
  }
  return (
    <section>
      <h2 className="mb-3 text-xl font-semibold tracking-[-0.4px] text-ink">Recent activity</h2>
      {loading ? (
        <div className={`${TABLE_WRAP} h-16 animate-pulse`} aria-label="Loading" />
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-rule bg-white px-5 py-6 text-[15px] text-muted">
          No {noun.many} have been sent to QuickBooks from Flow yet.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g[0]!.id} className={TABLE_WRAP}>
              <p className="border-b border-[#e7e7e4] bg-[#fafaf8] px-[18px] py-2.5 text-[14px] font-medium text-muted">
                {formatDateTime(g[0]!.occurredAt)} · {g.length} {g.length === 1 ? "attempt" : "attempts"}
              </p>
              <table className="w-full text-left">
                <tbody className="text-[15px]">
                  {g.map((h) => (
                    <tr key={h.id} className={TROW}>
                      <td className={cn(TD, "w-[38%] min-w-[280px] text-ink")}>
                        {h.docNumber ? docLabel(h.docNumber) : "—"}
                        {h.name ? <span className="text-muted"> · {h.name}</span> : null}
                      </td>
                      <td className={TD}>
                        <ActivityResult h={h} entity={entity} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ActivityResult({ h, entity }: { h: HistoryRow; entity: Entity }) {
  if (h.status === "success" && h.environment === "sandbox") return <span className="text-warn">{TEST_COMPANY_ONLY}</span>;
  if (h.status === "success" && h.message) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className={cn(PILL, PILL_TONE.warn)}>Sent — tax differs</span>
        {h.entityId && <WhyLink id={h.entityId} entity={entity} />}
      </span>
    );
  }
  if (h.status === "success") return <span className={cn(PILL, PILL_TONE.ok)}>Sent</span>;
  if (h.status === "retried") return <span className="text-muted">Failed — sent again later</span>;
  if (h.status === "pending") {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className={cn(PILL, PILL_TONE.warn)}>On hold</span>
        {h.headline && <span className="text-ink">{h.headline}</span>}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className={cn(PILL, PILL_TONE.risk)}>Didn't go through</span>
      {h.headline && <span className="text-ink">{h.headline}</span>}
      {h.entityId && <WhyLink id={h.entityId} entity={entity} />}
    </span>
  );
}
